import { safeWindow } from 'frontend/utils/window';
import { getEnvironmentForErrorReporting } from 'frontend/utils/environment';
import Vue, { CreateElement, RenderContext, VNode, PluginFunction } from 'vue';
import VueAwesomeSwiper from 'vue-awesome-swiper';
import VueMq from 'vue-mq';
import Router from 'vue-router';
import { sync } from 'vuex-router-sync';
import VueCompositionAPI from '@vue/composition-api';
import { VueQueryPlugin } from 'vue-query';
import FloatingVue from 'floating-vue';

import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';

import createRouter from './router';
import createStore from './store';
import App from './views/App.vue';

import { track, clickableHover } from 'frontend/utils/directives';

Vue.config.productionTip = false;

Vue.directive('track', track);
Vue.directive('clickable-hover', clickableHover);
Vue.directive('swiper', VueAwesomeSwiper.directive);

// create router
const router: Router = createRouter();

if (process.env.NODE_ENV === 'production') {
  const safeWin = safeWindow();

  const sentryEnvironment = getEnvironmentForErrorReporting();

  Sentry.init({
    Vue,
    dsn: 'https://621cfa37a828468ab6b77f0cc3bff7dd@o1307044.ingest.sentry.io/6551268',
    integrations: [
      new BrowserTracing(
        safeWin === undefined
          ? undefined
          : // routingInstrumentation depends on the existence of window so only include this if we are doing CSR
            { routingInstrumentation: Sentry.vueRouterInstrumentation(router) },
      ),
    ],
    tracesSampleRate: 0.01,
    environment: sentryEnvironment?.environment || 'production',
    ignoreErrors: [
      // Related to this dicussion: https://brilliant.slack.com/archives/C024679BNJH/p1700497294963139
      'Non-Error promise rejection captured',
    ],
  });

  if (sentryEnvironment) {
    Sentry.setTag('serverName', sentryEnvironment.serverName);
  }
}

//
// See https://ssr.vuejs.org/guide/data.html#data-store for explanations.
//
export function createApp(isSSR: boolean) {
  // create router and store instances
  const store = createStore(router);

  // sync so that route state is available as part of the store
  sync(store, router);

  Vue.use(VueCompositionAPI);
  Vue.use(VueQueryPlugin);

  // Header search
  // if (!isSSR) {
  //   import('vue-awesome-swiper').then((vueAwesomeSwiper) => {
  //     Vue.use(vueAwesomeSwiper);
  //   });
  // }

  // Media queries
  Vue.use(VueMq, {
    breakpoints: {
      mobile: 700,
      medium: 1200,
      desktop: Infinity,
    },
    defaultBreakpoint: 'desktop',
  });

  if (!isSSR) {
    import('vue-infinite-scroll').then(infiniteScroll => {
      Vue.use(infiniteScroll as unknown as PluginFunction<unknown>);
    });
  }

  // Vue wrapper for popper.js
  Vue.use(FloatingVue);

  // create the app instance, injecting both the router and the store
  const app = new Vue({
    router,
    store,
    render: (createElement: CreateElement, hack: RenderContext<Record<string, any>>): VNode =>
      createElement(App),
  });

  // expose the app, the router and the store.
  return { app, router, store };
}
